import React from "react";
import wingsImg from "@/assets/spotlight-wings.png";

/**
 * SpotlightWings — photorealistic 3D angel wings around a Spotlight
 * seller's avatar. Uses a high-fidelity PNG art asset (white feathers
 * with gold accents) supplied by the product owner.
 *
 * The PNG is imported through webpack so the URL resolves correctly
 * in both dev and prod builds. We inject that URL into a CSS variable
 * (`--wings-img`) so the masked gold-shine overlay in index.css can
 * read the SAME image as its mask source.
 *
 * Animations layered on top of the static asset:
 *   • **Slow flap** — the whole pair scales horizontally 1.0 ↔ 0.92 on
 *     a 4s loop so the wings look like they're slowly beating, not
 *     locked open.
 *   • **Float** — the wings bob ±5px on a 5.2s loop so they feel alive.
 *   • **Gold shine sweep** — an animated diagonal gradient masked to
 *     the wing silhouette (so it only paints on the feathers).
 *   • **Sparkle particles** — 8 small white twinkling stars float
 *     around the wing area, each with its own offset / duration, so
 *     the picture is constantly glittering.
 */
export default function SpotlightWings({ size = 140 }) {
  // 8 sparkle particles positioned around the wings.
  const SPARKLES = [
    { top: "8%",  left: "12%", delay: "0s",   dur: "2.4s" },
    { top: "18%", left: "84%", delay: "0.6s", dur: "2.8s" },
    { top: "38%", left: "6%",  delay: "1.1s", dur: "2.2s" },
    { top: "42%", left: "92%", delay: "0.3s", dur: "3.0s" },
    { top: "62%", left: "16%", delay: "1.5s", dur: "2.6s" },
    { top: "68%", left: "80%", delay: "0.9s", dur: "2.4s" },
    { top: "82%", left: "30%", delay: "1.8s", dur: "3.2s" },
    { top: "86%", left: "66%", delay: "0.4s", dur: "2.8s" },
  ];

  return (
    <div
      className="spotlight-wings"
      style={{
        width: size,
        height: size,
        position: "relative",
        pointerEvents: "none",
        // Expose the PNG URL to CSS so the shine layer can mask to it.
        "--wings-img": `url(${wingsImg})`,
      }}
      aria-hidden="true"
    >
      {/* Flap wrapper — the slow scaleX flap pulse lives here so we
          can keep float / breath separate on the inner layers. */}
      <div className="spotlight-wings-flap" style={{ width: "100%", height: "100%" }}>
        {/* Base layer — the photoreal wings PNG. */}
        <img
          src={wingsImg}
          alt=""
          draggable={false}
          className="spotlight-wings-img"
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            userSelect: "none",
            filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.45))",
          }}
        />

        {/* Gold shine overlay — diagonal gradient masked to the PNG's
            alpha (via --wings-img CSS variable). */}
        <span className="spotlight-wings-shine" aria-hidden="true" />
      </div>

      {/* Sparkle layer — sits ABOVE the wings so the twinkles read
          on top of the feathers. */}
      <div className="spotlight-wings-sparkles" aria-hidden="true">
        {SPARKLES.map((s, i) => (
          <span
            key={i}
            className="spotlight-wings-sparkle"
            style={{
              top: s.top,
              left: s.left,
              animationDelay: s.delay,
              animationDuration: s.dur,
            }}
          />
        ))}
      </div>
    </div>
  );
}
